import { useState, useEffect } from 'react';
import { Form, Modal, message, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import CustomButton from 'base/ui/components/Button';
import { SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { TransitionButtonModel } from 'models/PurchaseOrder';
import { sFormatNumber } from 'utils/formatHelper';

interface OutboundWeighingModalProps {
  open: boolean;
  onClose: () => void;
  buttonInfo: TransitionButtonModel;
  taskId: string;
  wasteHandoverRegistrationCode?: string;
  onUpdate: (outboundWeighingData?: { secondWeight: number; deliveredQuantity: number }) => Promise<boolean>;
  onExecute: (data: {
    taskId: string;
    statusTransitionId: string;
    comment?: string;
  }) => Promise<boolean>;
  onReload?: () => Promise<void>;
}

const OutboundWeighingModal: React.FC<OutboundWeighingModalProps> = ({
  open,
  onClose,
  buttonInfo,
  taskId,
  wasteHandoverRegistrationCode,
  onUpdate,
  onExecute,
  onReload,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Xử lý submit
  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const { secondWeight, deliveredQuantity } = values;

      // Bước 1: Update form trước với dữ liệu cân ra
      const updateSuccess = await onUpdate({
        secondWeight: secondWeight || 0,
        deliveredQuantity: deliveredQuantity || 0,
      });
      if (!updateSuccess) {
        setLoading(false);
        return;
      }

      // Bước 2: Thực thi transition
      const executeData: any = {
        taskId,
        statusTransitionId: buttonInfo.statusTransitionId,
      };

      const executeSuccess = await onExecute(executeData);

      if (executeSuccess) {
        // Reload data
        if (onReload) {
          await onReload();
        }
        handleClose();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error executing outbound weighing:', error);
      message.error(t('message.error'));
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  // Reset form khi modal mở/đóng
  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title={<h2 className="uppercase text-center text-base font-semibold">XÁC NHẬN ĐÃ CÂN ĐẦU RA</h2>}
      width={600}
      className="[&_.ant-modal-header]:!border-b-0 [&_.ant-modal-title]:!text-center"
    >
      <SearchForm
        form={form}
        onFinish={handleSubmit}
        column="col-1"
      >
        <div className='my-2'>
          <h2 className='text-center text-sm font-medium'>
            Xác nhận đã cân đầu ra xe giao hàng | {wasteHandoverRegistrationCode || ''}
          </h2>
        </div>

        {/* Trọng lượng cân lần 2 */}
        <SearchItem
          name="secondWeight"
          label="Trọng lượng cân lần 2"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <InputNumber
            min={0}
            precision={3}
            style={{ width: '100%' }}
            placeholder="Nhập trọng lượng cân lần 2"
            addonAfter="Kg"
            controls={false}
            formatter={(value, info) => {
              return sFormatNumber(Number(value)) as string;
            }}
          />
        </SearchItem>

        {/* Trọng lượng hàng thực tế */}
        <SearchItem
          name="deliveredQuantity"
          label="Trọng lượng hàng thực tế"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <InputNumber
            min={0}
            precision={3}
            style={{ width: '100%' }}
            placeholder="Nhập trọng lượng hàng thực tế"
            addonAfter="Kg"
            controls={false}
            formatter={(value, info) => {
              return sFormatNumber(Number(value)) as string;
            }}
          />
        </SearchItem>

        <div className='mt-4 flex justify-end gap-2'>
          <CustomButton
            type="default"
            htmlType="button"
            loading={loading}
            onClick={handleClose}
          >
            Thoát
          </CustomButton>
          <CustomButton
            type="primary"
            htmlType="submit"
            loading={loading}
          >
            Xác nhận
          </CustomButton>
        </div>
      </SearchForm>
    </Modal>
  );
};

export default OutboundWeighingModal;

